import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import SortableGrid from "../components/SortableGrid";
import { useAuth } from "../contexts/AuthContext";
import api, { API_BASE } from "../lib/api";
import { fmtCur, fmtDate, fmtNum } from "../lib/format";
import { ExportXlsxButton } from "../components/ExportXlsxButton";
import { xlsxDate } from "../lib/exportXlsx";

const curCode = (c) => (/(\$|US)/i.test(c || "") ? "USD" : (c || "USD"));

// FindTeu marks a finished journey as "Completed" — the business prefers "Delivered".
export const statusLabel = (s) => (/^completed$/i.test((s || "").trim()) ? "Delivered" : (s || "—"));

const STATUS_COLORS = {
  delivered: ["#DCFCE7", "#166534"],
  "in transit": ["#DBEAFE", "#1E40AF"],
  entered: ["#FEF3C7", "#92400E"],
};
export const StatusPill = ({ status, live }) => {
  const label = statusLabel(status);
  const [bg, fg] = STATUS_COLORS[label.toLowerCase()] || (live ? ["#DCFCE7", "#166534"] : ["#F1F5F9", "#475569"]);
  return (
    <span style={{ display: "inline-block", fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: bg, color: fg, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
};

const AccuracyPill = ({ live }) => (
  <span
    data-testid={live ? "eta-pill-live" : "eta-pill-db"}
    title={live ? "Fetched live from FindTeu" : "Estimated from the database (not yet tracked live)"}
    style={{
      fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, padding: "2px 7px", borderRadius: 999,
      textTransform: "uppercase", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4,
      background: live ? "#0F766E" : "#E2E8F0", color: live ? "white" : "#64748B",
    }}
  >
    {live && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#5EEAD4", boxShadow: "0 0 0 2px rgba(94,234,212,.35)" }} />}
    {live ? "Live" : "DB Est."}
  </span>
);

export default function Shipping() {
  const nav = useNavigate();  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isSuperAdmin = user?.role === "super_admin";
  const shipRowHref = (s) => (s.has_container
    ? `/shipping/${encodeURIComponent(s.container_no)}`
    : `/shipping/inv/${encodeURIComponent(s.inv_no)}`);
  const [lastRow] = useState(() => sessionStorage.getItem("ship:lastRow") || null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hook, setHook] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [trackableOnly, setTrackableOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all | awaiting | transit | delivered
  const [autotrack, setAutotrack] = useState(null); // FindTeu credits + untracked backlog

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get("/shipping/shipments")
      .then((r) => setRows(r.data || []))
      .catch((e) => setError(e.response?.data?.detail || e.message || "Failed to load shipments"))
      .finally(() => setLoading(false));
    if (isAdmin) api.get("/shipping/webhook/status").then((r) => setHook(r.data)).catch(() => {});
    if (isSuperAdmin) api.get("/shipping/autotrack/config").then((r) => setAutotrack(r.data)).catch(() => {});
  }, [isAdmin, isSuperAdmin]);

  const [dlReport, setDlReport] = useState(false);
  const [reportPcode, setReportPcode] = useState("MSI");
  // Cross-FY "find any invoice" — the table itself only holds the current-FY window, so an older
  // invoice is fetched on demand from the server and merged in (deduped by inv_no).
  const [findQ, setFindQ] = useState("");
  const [finding, setFinding] = useState(false);
  const findAnyInvoice = async () => {
    const q = findQ.trim();
    if (q.length < 2) { toast.info("Type at least 2 characters (invoice #, party or PO)"); return; }
    setFinding(true);
    try {
      const { data } = await api.get(`/shipping/search?q=${encodeURIComponent(q)}`);
      if (!data || !data.length) { toast.info(`No invoice found for “${q}”`); return; }
      setRows((prev) => {
        const have = new Set(prev.map((r) => r.inv_no));
        const add = (data || []).filter((r) => !have.has(r.inv_no));
        return add.length ? [...add, ...prev] : prev;
      });
      const already = data.every((r) => rows.some((p) => p.inv_no === r.inv_no));
      toast.success(`Found ${data.length} invoice(s) for “${q}”${already ? " (already in the list)" : " — added to the table"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally { setFinding(false); }
  };
  const downloadStatusReport = async () => {
    setDlReport(true);
    try {
      const { data } = await api.get(
        `/ms/reports/customer-status.xlsx?pcode=${reportPcode}&_=${Date.now()}`,
        { responseType: "blob", headers: { "Cache-Control": "no-cache" } });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Order_Status_Report_${reportPcode}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${reportPcode} Customer Order Status report downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build the status report");
    } finally {
      setDlReport(false);
    }
  };

  const registerHook = async () => {
    setRegistering(true);
    try {
      const base = API_BASE.replace(/\/api$/, "");
      const { data } = await api.post("/shipping/webhook/register", { base_url: base });
      toast.success("Auto-updates enabled — FindTeu will push container movements automatically");
      const r = await api.get("/shipping/webhook/status");
      setHook(r.data);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not enable auto-updates");
    } finally {
      setRegistering(false);
    }
  };

  // ── Untracked-backlog drill-down + one-click "track now within remaining credits" (super_admin)
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [backlog, setBacklog] = useState(null); // { count, remaining, reserve, trackable_now, items }
  const [backlogLoading, setBacklogLoading] = useState(false);
  const [tracking, setTracking] = useState(false);

  const loadBacklog = async () => {
    setBacklogLoading(true);
    try {
      const { data } = await api.get("/shipping/autotrack/backlog");
      setBacklog(data);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the untracked backlog");
    } finally {
      setBacklogLoading(false);
    }
  };

  const openBacklog = () => { setBacklogOpen(true); loadBacklog(); };

  const trackNow = async () => {
    setTracking(true);
    try {
      await api.post("/shipping/autotrack/run");
      toast.success("Tracking started — first-tracking untracked containers within remaining credits");
      // Poll the backlog a few times as the background sweep works through the queue.
      for (let i = 0; i < 8; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const d = await loadBacklog();
        if (d && d.count === 0) break;
      }
      // Refresh the header badge too.
      api.get("/shipping/autotrack/config").then((r) => setAutotrack(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start tracking");
    } finally {
      setTracking(false);
    }
  };

  // ── Booking-tracking backfill (super_admin): track every shipment that has a carrier booking #
  // in the ERP by booking # (voyage-specific, immune to container reuse).
  const [bkfOpen, setBkfOpen] = useState(false);
  const [bkf, setBkf] = useState(null);          // preview {count, pending, remaining, trackable_now, rows}
  const [bkfLoading, setBkfLoading] = useState(false);
  const [bkfRunning, setBkfRunning] = useState(false);
  const [bkfRan, setBkfRan] = useState(false);   // a run happened in THIS session (show its results)
  const [bkfStatus, setBkfStatus] = useState(null); // {running, results, summary, progress}

  const loadBkf = async () => {
    setBkfLoading(true);
    try {
      const { data } = await api.get("/shipping/booking-backfill/preview");
      setBkf(data);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load booking backfill");
    } finally {
      setBkfLoading(false);
    }
  };

  const openBkf = () => { setBkfOpen(true); setBkf(null); setBkfStatus(null); setBkfRan(false); loadBkf(); };

  const runBkf = async () => {
    setBkfRunning(true);
    setBkfRan(true);
    try {
      const r = await api.post("/shipping/booking-backfill/run");
      if (r.data?.status === "already_running") toast.info("A booking backfill is already running");
      else toast.success("Booking backfill started — tracking by carrier booking # within pooled credits");
      for (let i = 0; i < 60; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        const st = await api.get("/shipping/booking-backfill/status");
        setBkfStatus(st.data);
        if (st.data && !st.data.running && st.data.summary) break;
      }
      await loadBkf();
      api.get("/shipping/autotrack/config").then((r2) => setAutotrack(r2.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the booking backfill");
    } finally {
      setBkfRunning(false);
    }
  };

  // Rebuilt for how a user actually reads a shipment row, left→right:
  //   WHO/WHAT it is → WHERE it goes → WHEN/STATE → HOW MUCH.
  // The container # is a physical reference (kept, but demoted beneath the customer — it is
  // no longer the leading anchor). Related fields are paired to cut 12 columns down to 9.
  const chip = (text, bg, fg) => (
    <span style={{ fontSize: 9.5, fontWeight: 700, padding: "1px 6px", borderRadius: 5, background: bg, color: fg, whiteSpace: "nowrap" }}>{text}</span>
  );
  const etaOf = (s) => ((s.live && s.live_eta) ? s.live_eta : s.eta_date);
  const deliveredDateOf = (s) => (isDelivered(s) ? (s.live_delivered_date || s.delivery_date || null) : null);
  const isDelivered = (s) => /^delivered$/i.test(statusLabel((s.live && s.live_status) ? s.live_status : s.status));
  const daysToGo = (s) => {
    const eta = etaOf(s);
    if (!eta) return null;
    const d = new Date(eta);
    if (isNaN(d.getTime())) return null;
    return Math.ceil((d - new Date(new Date().toDateString())) / 86400000);
  };
  const columns = [
    // 1 — CUSTOMER: the END customer is the anchor. When the shipment is billed to a transfer
    //     party (e.g. AV MATICS), we resolve the real customer (e.g. GEOTEK) via dbo.party.TP_CODE
    //     and show the TP small beneath. Container # + size sit below as physical reference.
    { key: "party_name", header: "Customer", accessor: (s) => s.end_customer_name || s.party_name,
      searchAccessor: (s) => `${s.end_customer_name || ""} ${s.party_name || ""} ${s.tp_name || ""} ${s.container_no || ""} ${s.eseal || ""} ${s.line_seal || ""}`,
      render: (s) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {(s.end_customer_name || s.party_name) ? (
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>{s.end_customer_name || s.party_name}</span>
          ) : (
            <span data-testid={`ship-no-customer-${s.inv_no}`} title="This invoice has no customer/party set in the ERP (dbo.invoice). Assign the customer in the ERP to fix."
                  style={{ fontSize: 11, fontWeight: 700, color: "#B45309", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 4, padding: "1px 6px", width: "fit-content" }}>
              ⚠ No customer in ERP
            </span>
          )}
          {s.is_tp && s.tp_name && (
            <span style={{ fontSize: 10, color: "#94A3B8", fontWeight: 600 }} data-testid={`ship-tp-${s.inv_no}`}>via TP · {s.tp_name}</span>
          )}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            {s.has_container ? (
              <>
                <span className="text-mono" style={{ fontSize: 10.5, color: "#64748B" }} data-testid={`ship-cno-${s.inv_no}`}>{s.container_no}</span>
                {s.cont_size && chip(s.cont_size, "#E0E7FF", "#3730A3")}
              </>
            ) : (
              <span data-testid={`ship-nocont-${s.inv_no}`} style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 0.3, padding: "2px 7px", borderRadius: 5, background: "#FEF3C7", color: "#92400E", textTransform: "uppercase" }}>No container</span>
            )}
          </span>
        </div>
      ) },
    // 2 — WHAT: invoice number + its date. Sorts by date; searchable by invoice #.
    { key: "inv_no", header: "Invoice", type: "date", accessor: (s) => s.sale_date,
      searchAccessor: (s) => s.inv_no,
      render: (s) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span className="text-mono" style={{ fontSize: 11.5, fontWeight: 600 }}>{s.inv_no || "—"}</span>
          <span className="text-mono muted" style={{ fontSize: 10 }}>{fmtDate(s.sale_date)}</span>
        </div>
      ) },
    // 3 — kind of shipment (Export / Transfer-party / etc.)
    { key: "ship_type", header: "Type", accessor: (s) => s.ship_type,
      render: (s) => s.ship_type ? chip(s.ship_type, "#EEF2FF", "#4338CA") : <span className="muted">—</span> },
    // 4 — ETA (live from FindTeu when tracked, else DB estimate) + accuracy pill.
    { key: "eta_date", header: "ETA", type: "date", accessor: (s) => etaOf(s),
      render: (s) => (
        !s.has_container ? <span className="muted">—</span> :
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span className="text-mono" style={{ fontSize: 11.5, fontWeight: 600 }}>{fmtDate(etaOf(s))}</span>
          <AccuracyPill live={s.live} />
        </span>
      ) },
    // 5 — Actual delivery date (live completion event, else DB deliverydate). Blank until delivered.
    { key: "delivered", header: "Actual Delivery", type: "date", accessor: (s) => deliveredDateOf(s),
      render: (s) => {
        const dd = deliveredDateOf(s);
        return dd
          ? <span className="text-mono" data-testid={`ship-delivered-${s.inv_no}`} style={{ fontSize: 11.5, fontWeight: 700, color: "#166534" }}>{fmtDate(dd)}</span>
          : <span className="muted">—</span>;
      } },
    // 6 — STATE: Awaiting container / In-Transit / Delivered + a days-to-go pill while in transit.
    { key: "status", header: "Status", accessor: (s) => (!s.has_container ? "Awaiting container" : isDelivered(s) ? "Delivered" : "In-Transit"),
      render: (s) => {
        if (!s.has_container) {
          return <span data-testid={`ship-status-${s.inv_no}`}
                       style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "#FEF3C7", color: "#92400E", whiteSpace: "nowrap" }}>Awaiting container</span>;
        }
        if (isDelivered(s)) {
          return <span data-testid={`ship-status-${s.inv_no}`}
                       style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "#DCFCE7", color: "#166534", whiteSpace: "nowrap" }}>Delivered</span>;
        }
        const dtg = daysToGo(s);
        let daysPill = null;
        if (dtg != null) {
          const [bg, fg] = dtg < 0 ? ["#FEE2E2", "#B91C1C"] : dtg <= 7 ? ["#FEF3C7", "#92400E"] : ["#F1F5F9", "#475569"];
          const txt = dtg < 0 ? `${Math.abs(dtg)}d overdue` : dtg === 0 ? "due today" : `${dtg}d to go`;
          daysPill = <span style={{ fontSize: 9.5, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: bg, color: fg, whiteSpace: "nowrap" }}>{txt}</span>;
        }
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexWrap: "wrap" }} data-testid={`ship-status-${s.inv_no}`}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "#DBEAFE", color: "#1E40AF", whiteSpace: "nowrap" }}>In-Transit</span>
            {daysPill}
          </span>
        );
      } },
    // 6b — OTD: expected delivery (ETA + inland transit) vs the PO line due date. Blank when the row
    //      has no live ETA or no matched PO due date. Worst (most-late) line drives the row.
    { key: "otd", header: "OTD",
      accessor: (s) => (["late", "on_time", "early"].includes(s.otd_status) ? (s.otd_delta_days ?? 0) : null),
      render: (s) => {
        if (!["late", "on_time", "early"].includes(s.otd_status)) return <span className="muted">—</span>;
        const d = s.otd_delta_days;
        const [bg, fg] = s.otd_status === "late" ? ["#FEE2E2", "#B91C1C"] : ["#DCFCE7", "#166534"];
        const txt = s.otd_status === "late" ? `Late ${d}d` : s.otd_status === "early" ? `Early ${Math.abs(d)}d` : "On-time";
        return <span data-testid={`ship-otd-${s.inv_no}`} title="Expected delivery (FindTeu port ETA + inland transit) vs PO due date"
          style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: bg, color: fg, whiteSpace: "nowrap" }}>{txt}</span>;
      } },
    // 7-10 — HOW MUCH: cartons/pallets, gross weight, net weight, value.
    { key: "packing", header: "Cartons / Pallets", align: "right", accessor: (s) => s.pack_qty,
      render: (s) => s.pack_qty
        ? <span className="text-mono" style={{ fontSize: 11.5 }}>{fmtNum(s.pack_qty)}{s.pack_type ? ` ${s.pack_type}${s.pack_qty > 1 ? "s" : ""}` : ""}</span>
        : <span className="muted">—</span> },
    { key: "gross_wt", header: "Gross Wt (kg)", align: "right", accessor: (s) => s.gross_wt,
      render: (s) => <span className="text-mono" style={{ fontSize: 11.5 }}>{s.gross_wt ? fmtNum(s.gross_wt) : "—"}</span> },
    { key: "net_wt", header: "Net Wt (kg)", align: "right", accessor: (s) => s.net_wt,
      render: (s) => <span className="text-mono" style={{ fontSize: 11.5 }}>{s.net_wt ? fmtNum(s.net_wt) : "—"}</span> },
    { key: "amount", header: "Amount", align: "right", accessor: (s) => s.amount,
      render: (s) => <span className="text-mono" style={{ fontWeight: 700, fontSize: 12 }}>{fmtCur(s.amount, curCode(s.currency))}</span> },
  ];

  const exportColumns = [
    { header: "Customer", value: (s) => s.end_customer_name || s.party_name || "" },
    { header: "Via TP", value: (s) => (s.is_tp ? (s.tp_name || "") : "") },
    { header: "Invoice #", value: (s) => s.inv_no || "" },
    { header: "Container #", value: (s) => s.container_no || "" },
    { header: "Container Size", value: (s) => s.cont_size || "" },
    { header: "Type", value: (s) => s.ship_type || "" },
    { header: "Status", value: (s) => statusLabel(s.status) },
    { header: "POL", value: (s) => s.pol || "" },
    { header: "POD", value: (s) => s.pod || "" },
    { header: "ETD", value: (s) => xlsxDate(s.etd_date) },
    { header: "ETA", value: (s) => xlsxDate(s.eta_date) },
    { header: "Sale Date", value: (s) => xlsxDate(s.sale_date) },
    { header: "Packing", value: (s) => (s.pack_qty ? `${s.pack_qty} ${s.pack_type || ""}`.trim() : "") },
    { header: "Gross Wt (kg)", value: (s) => Number(s.gross_wt) || "" },
    { header: "Net Wt (kg)", value: (s) => Number(s.net_wt) || "" },
    { header: "Amount", value: (s) => Number(s.amount) || 0 },
    { header: "Currency", value: (s) => curCode(s.currency) },
  ];

  const trackableCount = rows.filter((r) => r.has_container).length;
  // Delivery state of a row, reusing the same rules the Status column renders.
  const stateOf = (s) => (!s.has_container ? "awaiting" : isDelivered(s) ? "delivered" : "transit");
  const stateCounts = {
    all: rows.length,
    awaiting: rows.filter((r) => stateOf(r) === "awaiting").length,
    transit: rows.filter((r) => stateOf(r) === "transit").length,
    delivered: rows.filter((r) => stateOf(r) === "delivered").length,
    atrisk: rows.filter((r) => r.otd_status === "late").length,
  };
  const matchStatus = (r) =>
    statusFilter === "all" ? true
      : statusFilter === "atrisk" ? r.otd_status === "late"
      : stateOf(r) === statusFilter;
  const visibleRows = useMemo(() => rows.filter((r) =>
    (!trackableOnly || r.has_container) && matchStatus(r)), [rows, trackableOnly, statusFilter]);
  // Rows the grid is ACTUALLY showing (after its internal search + date-range filter). The grid
  // reports these up so the "Export" button downloads exactly what's on screen, not everything.
  const [exportRows, setExportRows] = useState(visibleRows);

  return (
    <Layout breadcrumb={["Home", "Shipping"]}>
      <div className="jde-page">
        <div className="jde-panel">
          <div className="jde-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="jde-panel-title">
              SHIPPING &amp; INVOICES ({rows.length}{loading ? "…" : ""})
              {!loading && rows.some((r) => !r.has_container) && (
                <span data-testid="ship-missing-count" style={{ marginLeft: 10, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.3, padding: "2px 8px", borderRadius: 999, background: "#FEF3C7", color: "#92400E", textTransform: "uppercase" }}>
                  {rows.filter((r) => !r.has_container).length} awaiting container
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end", rowGap: 8, flex: "1 1 auto", minWidth: 0 }}>
            <div data-testid="ship-status-filter" style={{ display: "inline-flex", border: "1px solid var(--jde-border, #E2E8F0)", borderRadius: 8, overflow: "hidden" }}>
              {[
                { k: "all", label: "All" },
                { k: "awaiting", label: "Awaiting" },
                { k: "transit", label: "In-Transit" },
                { k: "delivered", label: "Delivered" },
                { k: "atrisk", label: "At-Risk" },
              ].map((o, i) => {
                const active = statusFilter === o.k;
                const activeBg = o.k === "atrisk" ? "#B91C1C" : "#1E40AF";
                return (
                  <button key={o.k} data-testid={`ship-status-filter-${o.k}`}
                    onClick={() => setStatusFilter(o.k)}
                    title={`Show ${o.label} shipments`}
                    style={{
                      border: "none", cursor: "pointer", padding: "6px 11px", fontSize: 11.5, fontWeight: 700,
                      borderLeft: i === 0 ? "none" : "1px solid var(--jde-border, #E2E8F0)",
                      background: active ? activeBg : "#fff",
                      color: active ? "#fff" : (o.k === "atrisk" && stateCounts.atrisk > 0 ? "#B91C1C" : "#475569"),
                      transition: "background-color .15s ease, color .15s ease",
                    }}>
                    {o.label} <span style={{ opacity: 0.8, fontWeight: 600 }}>({stateCounts[o.k]})</span>
                  </button>
                );
              })}
            </div>
            <button data-testid="ship-trackable-toggle" onClick={() => setTrackableOnly((v) => !v)}
              className={`jde-btn jde-btn-sm ${trackableOnly ? "jde-btn-primary" : ""}`}
              title="Show only shipments that have a container number (trackable)">
              {trackableOnly ? "Trackable only" : "Trackable"} ({trackableCount})
            </button>
            <div data-testid="ship-find-invoice" style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              <input data-testid="ship-find-invoice-input" value={findQ}
                onChange={(e) => setFindQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") findAnyInvoice(); }}
                placeholder="Find any invoice # (any date)…"
                className="jde-input jde-input-sm" style={{ height: 30, fontSize: 12, width: 190 }}
                title="Search a commercial invoice by number / party / PO across ALL dates — even outside the current financial year" />
              <button data-testid="ship-find-invoice-btn" className="jde-btn jde-btn-sm" onClick={findAnyInvoice} disabled={finding}
                title="Find an invoice outside the current-FY window and add it to the table">
                {finding ? "Finding…" : "Find"}
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }} data-testid="status-report-group">
              <select data-testid="status-report-customer" value={reportPcode} onChange={(e) => setReportPcode(e.target.value)}
                className="jde-input jde-input-sm" style={{ height: 30, fontSize: 12, width: 82, flex: "0 0 auto" }} title="Customer for the Order Status report">
                <option value="MSI">MSI</option>
                <option value="MPST">MPST</option>
              </select>
              <button data-testid="ship-msi-status-report" className="jde-btn jde-btn-sm jde-btn-primary" onClick={downloadStatusReport} disabled={dlReport} title="Download the Customer Order Status report (Excel)">
                {dlReport ? "Building…" : "Status Report"}
              </button>
            </div>
            <ExportXlsxButton filename="shipping" columns={exportColumns} rows={exportRows} sheetName="Shipping" testid="ship-export-xlsx" />
            {isSuperAdmin && autotrack && (
              <div data-testid="ship-findteu-credits" title="FindTeu monthly credits and the number of eligible containers still awaiting their first track (within the auto-track lookback window)."
                style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
                <span data-testid="ship-findteu-credits-remaining" className="jde-badge"
                  style={{ background: "#EEF2FF", color: "#3730A3", fontWeight: 800 }}>
                  {autotrack.credits && autotrack.credits.remaining != null
                    ? `${autotrack.credits.remaining} credits left`
                    : "credits n/a"}
                  {autotrack.credits && autotrack.credits.monthly_limit != null
                    ? ` / ${autotrack.credits.monthly_limit}`
                    : ""}
                </span>
                <button data-testid="ship-findteu-backlog" onClick={openBacklog}
                  title="Click to view the untracked containers and track them within remaining credits"
                  className="jde-badge"
                  style={{
                    background: (autotrack.untracked_backlog || 0) > 0 ? "#FEF3C7" : "#DCFCE7",
                    color: (autotrack.untracked_backlog || 0) > 0 ? "#92400E" : "#166534",
                    fontWeight: 800, border: "none", cursor: "pointer",
                  }}>
                  {autotrack.untracked_backlog == null
                    ? "backlog n/a"
                    : `${autotrack.untracked_backlog} untracked backlog`}
                </button>
                <button data-testid="ship-booking-backfill-open" onClick={openBkf}
                  title="Track every shipment that has a carrier booking # in the ERP by booking # (voyage-specific, immune to container reuse)"
                  className="jde-badge"
                  style={{ background: "#E0F2FE", color: "#075985", fontWeight: 800, border: "none", cursor: "pointer" }}>
                  Booking backfill
                </button>
              </div>
            )}
            {isAdmin && hook?.configured && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11 }} data-testid="ship-webhook-control">
                {hook.webhook_url
                  ? <span className="jde-badge confirmed" title={hook.webhook_url}>Auto-updates ON</span>
                  : <span className="jde-badge" style={{ background: "#FEF3C7", color: "#92400E" }}>Auto-updates OFF</span>}
                {hook.plan && <span className="muted">FindTeu {hook.plan} · {hook.used_this_month}/{hook.monthly_limit} used</span>}
                <button data-testid="ship-webhook-register" className="jde-btn jde-btn-sm" onClick={registerHook} disabled={registering}>
                  {registering ? "…" : (hook.webhook_url ? "Re-register" : "Enable auto-updates")}
                </button>
              </div>
            )}
            </div>
          </div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            {error ? (
              <div data-testid="shipping-error" style={{ padding: "28px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#B91C1C", marginBottom: 6 }}>
                  Shipping database is currently unavailable
                </div>
                <div className="muted" style={{ fontSize: 13, maxWidth: 640, margin: "0 auto 14px" }}>
                  The container database could not be reached, so shipments can't be loaded right now.
                  This is usually a temporary connection issue with the shipping server.
                </div>
                <div style={{ fontSize: 11.5, color: "#94A3B8", fontFamily: "var(--jde-font-mono)", marginBottom: 14 }}>{error}</div>
                <button className="jde-btn jde-btn-sm" data-testid="shipping-retry" onClick={() => {
                  setLoading(true); setError(null);
                  api.get("/shipping/shipments").then((r) => setRows(r.data || []))
                    .catch((e) => setError(e.response?.data?.detail || e.message || "Failed to load shipments"))
                    .finally(() => setLoading(false));
                }}>Retry</button>
              </div>
            ) : (
              <div className="ship-grid-compact">
                <style>{`
                  .ship-grid-compact .jde-grid { font-size: 11.5px; }
                  .ship-grid-compact .jde-grid th, .ship-grid-compact .jde-grid td { padding: 5px 8px; }
                  .ship-grid-compact .ship-row-nocont td { background: #FFFBEB; }
                  .ship-grid-compact .ship-row-nocont:hover td { background: #FEF3C7; }
                  .ship-grid-compact .ship-row-nocont td:first-child { box-shadow: inset 3px 0 0 #F59E0B; }
                `}</style>
                <SortableGrid
                  columns={columns}
                  rows={visibleRows}
                  onFilteredRowsChange={setExportRows}
                  getRowKey={(s) => s.inv_no}
                  getRowClassName={(s) => (s.has_container ? "" : "ship-row-nocont")}
                  getRowHref={shipRowHref}
                  highlightKey={lastRow}
                  persistKey="shipping"
                  onRowClick={(s) => { sessionStorage.setItem("ship:lastRow", String(s.inv_no)); nav(shipRowHref(s)); }}
                  defaultSort={{ key: "inv_no", dir: "desc" }}
                  searchPlaceholder="Search customer, TP, container #, invoice #, seal…"
                  testid="shipping-table"
                  pageSize={100}
                  emptyMessage={loading ? "Loading invoices…" : (statusFilter !== "all" ? `No ${statusFilter === "transit" ? "in-transit" : statusFilter === "atrisk" ? "at-risk (late)" : statusFilter} shipments.` : (trackableOnly ? "No trackable shipments (none have a container number yet)." : "No export invoices in the current financial year. Use ‘Find any invoice #’ to pull an older one."))}
                />
              </div>
            )}
          </div>
        </div>

        {backlogOpen && (
          <div data-testid="ship-backlog-modal" onClick={() => setBacklogOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1000,
                     display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff", borderRadius: 12, width: "min(760px, 96vw)", maxHeight: "86vh",
                       display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,.3)" }}>
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #E2E8F0", display: "flex",
                            alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#0F172A" }}>Untracked container backlog</div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    Eligible containers (last {autotrack?.lookback_days || 120} days) not yet tracked in FindTeu.
                  </div>
                </div>
                <button data-testid="ship-backlog-close" className="jde-btn jde-btn-sm" onClick={() => setBacklogOpen(false)}>Close</button>
              </div>

              <div style={{ padding: "0", overflow: "auto", flex: 1 }}>
                {backlogLoading && !backlog ? (
                  <div className="muted" style={{ padding: 28, textAlign: "center", fontSize: 13 }}>Loading backlog…</div>
                ) : (backlog && backlog.count === 0) ? (
                  <div data-testid="ship-backlog-empty" style={{ padding: 36, textAlign: "center" }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#166534" }}>Backlog is clear</div>
                    <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
                      Every eligible container in the window is already tracked. You can safely downgrade the FindTeu plan.
                    </div>
                  </div>
                ) : (
                  <table data-testid="ship-backlog-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ position: "sticky", top: 0, background: "#F8FAFC", textAlign: "left" }}>
                        <th style={{ padding: "10px 14px", fontWeight: 700, color: "#475569" }}>Container #</th>
                        <th style={{ padding: "10px 14px", fontWeight: 700, color: "#475569" }}>Customer</th>
                        <th style={{ padding: "10px 14px", fontWeight: 700, color: "#475569" }}>Pcode</th>
                        <th style={{ padding: "10px 14px", fontWeight: 700, color: "#475569" }}>Entered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(backlog?.items || []).map((c) => (
                        <tr key={c.container_no} style={{ borderTop: "1px solid #EEF2F6" }}>
                          <td style={{ padding: "9px 14px", fontFamily: "var(--jde-font-mono)", fontWeight: 700 }}>{c.container_no}</td>
                          <td style={{ padding: "9px 14px" }}>{c.party || "—"}</td>
                          <td style={{ padding: "9px 14px", color: "#64748B" }}>{c.pcode || "—"}</td>
                          <td style={{ padding: "9px 14px", color: "#64748B" }}>{c.vdate ? c.vdate.slice(0, 10) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div style={{ padding: "14px 20px", borderTop: "1px solid #E2E8F0", display: "flex",
                            alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div className="muted" style={{ fontSize: 12 }} data-testid="ship-backlog-stats">
                  {backlog ? (
                    <>
                      <strong style={{ color: "#0F172A" }}>{backlog.count}</strong> untracked ·{" "}
                      <strong style={{ color: "#0F172A" }}>{backlog.remaining == null ? "—" : backlog.remaining}</strong> credits left
                      {backlog.reserve ? ` (reserve ${backlog.reserve})` : ""}
                    </>
                  ) : "…"}
                </div>
                <button data-testid="ship-backlog-track-now" className="jde-btn jde-btn-sm jde-btn-primary"
                  onClick={trackNow}
                  disabled={tracking || backlogLoading || !backlog || backlog.count === 0 || !backlog.trackable_now}
                  title={backlog && !backlog.trackable_now ? "No credits available to track more this month" : "First-track the backlog within remaining credits"}>
                  {tracking
                    ? "Tracking…"
                    : `Track now within remaining credits${backlog && backlog.trackable_now ? ` (${backlog.trackable_now})` : ""}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {bkfOpen && (() => {
          const disp = (bkfRan && bkfStatus?.results && bkfStatus.results.length) ? bkfStatus.results : (bkf?.rows || []);
          const STBADGE = {
            pending: ["#FEF3C7", "#92400E", "Will track"],
            tracked: ["#DCFCE7", "#166534", "Tracked ✓"],
            tracked_container: ["#D1FAE5", "#065F46", "Tracked (container)"],
            already_booking: ["#E0E7FF", "#3730A3", "Already booking"],
            frozen: ["#F1F5F9", "#475569", "Frozen (delivered)"],
            unknown_line: ["#FEE2E2", "#991B1B", "Unknown line"],
            no_credits: ["#FEE2E2", "#991B1B", "No credits"],
            error: ["#FEE2E2", "#991B1B", "Error"],
          };
          const sm = bkfRan ? bkfStatus?.summary : null;
          const prog = bkfStatus?.progress;
          return (
            <div data-testid="ship-booking-backfill-modal" onClick={() => !bkfRunning && setBkfOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1000,
                       display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
              <div onClick={(e) => e.stopPropagation()}
                style={{ background: "#fff", borderRadius: 12, width: "min(920px, 97vw)", maxHeight: "88vh",
                         display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,.3)" }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #E2E8F0", display: "flex",
                              alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#0F172A" }}>Backfill booking tracking</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                      Only your tracked customers — MacLean (MPY/MPST/MSI), GEOTEK (via AV MATICS), Valmont — with a carrier booking # + line in the ERP. Voyage-specific (immune to container reuse).
                    </div>
                  </div>
                  <button data-testid="ship-booking-backfill-close" className="jde-btn jde-btn-sm"
                    disabled={bkfRunning} onClick={() => setBkfOpen(false)}>Close</button>
                </div>

                <div style={{ padding: 0, overflow: "auto", flex: 1 }}>
                  {bkfLoading && !bkf ? (
                    <div className="muted" style={{ padding: 28, textAlign: "center", fontSize: 13 }}>Loading booking shipments…</div>
                  ) : (disp.length === 0) ? (
                    <div data-testid="ship-booking-backfill-empty" style={{ padding: 36, textAlign: "center" }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#475569" }}>No booking shipments found</div>
                      <div className="muted" style={{ fontSize: 13, marginTop: 6 }}>
                        No shipments in the window have a Booking # + ShippingLine in the ERP (dbo.Container). Populate those to enable booking tracking.
                      </div>
                    </div>
                  ) : (
                    <table data-testid="ship-booking-backfill-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ position: "sticky", top: 0, background: "#F8FAFC", textAlign: "left" }}>
                          {["Invoice #", "Customer", "Container #", "Booking #", "Line", "Status", "ETA"].map((h) => (
                            <th key={h} style={{ padding: "10px 12px", fontWeight: 700, color: "#475569" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {disp.map((r) => {
                          const st = r.result || r.status;
                          const [bg, fg, label] = STBADGE[st] || ["#F1F5F9", "#475569", st];
                          return (
                            <tr key={`${r.inv_no}-${r.container_no}`} style={{ borderTop: "1px solid #EEF2F6" }}>
                              <td style={{ padding: "9px 12px", fontFamily: "var(--jde-font-mono)", fontWeight: 700 }}>{r.inv_no || "—"}</td>
                              <td style={{ padding: "9px 12px" }}>{r.customer || r.party || "—"}</td>
                              <td style={{ padding: "9px 12px", fontFamily: "var(--jde-font-mono)" }}>{r.container_no}</td>
                              <td style={{ padding: "9px 12px", fontFamily: "var(--jde-font-mono)" }}>{r.booking_no || "—"}</td>
                              <td style={{ padding: "9px 12px", color: "#64748B" }}>{r.line || "—"}</td>
                              <td style={{ padding: "9px 12px" }}>
                                <span style={{ fontSize: 10.5, fontWeight: 800, padding: "2px 7px", borderRadius: 5, background: bg, color: fg, whiteSpace: "nowrap" }}
                                  title={r.error || (r.tracking_status ? `Live: ${r.tracking_status}` : "")}>{label}</span>
                                {st === "error" && r.error && (
                                  <div style={{ fontSize: 10, color: "#991B1B", marginTop: 3, maxWidth: 240, lineHeight: 1.3 }}>
                                    {String(r.error).replace(/^FindTeu booking [^:]*:\s*/i, "")}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: "9px 12px", color: "#334155" }}>{(r.eta_date || "").slice(0, 10) || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                <div style={{ padding: "14px 20px", borderTop: "1px solid #E2E8F0", display: "flex",
                              alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div className="muted" style={{ fontSize: 12 }} data-testid="ship-booking-backfill-stats">
                    {sm ? (
                      <>
                        <strong style={{ color: "#166534" }}>{sm.tracked}</strong> tracked ·{" "}
                        <strong style={{ color: "#0F172A" }}>{sm.skipped}</strong> skipped{sm.errors ? <> · <strong style={{ color: "#991B1B" }}>{sm.errors}</strong> errors</> : null}
                      </>
                    ) : prog && bkfStatus?.running ? (
                      <>Tracking… {prog.tracked}/{prog.total} done</>
                    ) : bkf ? (
                      <>
                        <strong style={{ color: "#0F172A" }}>{bkf.pending}</strong> to track ·{" "}
                        <strong style={{ color: "#0F172A" }}>{bkf.count}</strong> booking shipments ·{" "}
                        <strong style={{ color: "#0F172A" }}>{bkf.remaining == null ? "—" : bkf.remaining}</strong> pooled credits
                      </>
                    ) : "…"}
                  </div>
                  <button data-testid="ship-booking-backfill-run" className="jde-btn jde-btn-sm jde-btn-primary"
                    onClick={runBkf}
                    disabled={bkfRunning || bkfLoading || !bkf || !bkf.pending || !bkf.trackable_now}
                    title={bkf && !bkf.trackable_now ? "No pooled credits available to track more this month" : "Track all pending booking shipments within pooled credits"}>
                    {bkfRunning ? "Tracking…" : `Track by booking #${bkf && bkf.trackable_now ? ` (${bkf.trackable_now})` : ""}`}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </Layout>
  );
}
